import type { BalanceCardItem } from '@shared/components/panels/components/domain-sections/BalanceCardsSection';
import type { PaymentDetail } from '@shared/components/panels/types/panel-common.types';
import type { EventData } from '@shared/types/events.types';
import type { RiskCheck } from '@shared/types/risk.types';
import type { BaseTimelineEvent } from '@shared/components/panels/components/domain-sections/Timeline/types';
import type { TransactionData } from '@shared/components/panels/components/domain-sections/TransactionsSection';

// Balance Data
export const TERMINAL_BALANCE_DATA: BalanceCardItem[] = [
  {
    id: 'processing',
    label: 'Processing',
    totalBalance: '$24,567.89',
    balances: [
      { currency: 'USD', amount: 18234.56, formatted: '$18,234.56' },
      { currency: 'EUR', amount: 4122.87, formatted: '€4,122.87' },
      { currency: 'GBP', amount: 1654.23, formatted: '£1,654.23' },
      { currency: 'CAD', amount: 556.23, formatted: 'C$556.23' }
    ],
    status: 'processing',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-900'
  },
  {
    id: 'settlement',
    label: 'Settlement',
    totalBalance: '$127,340.15',
    balances: [
      { currency: 'USD', amount: 89456.78, formatted: '$89,456.78' },
      { currency: 'EUR', amount: 23891.45, formatted: '€23,891.45' },
      { currency: 'GBP', amount: 11234.67, formatted: '£11,234.67' },
      { currency: 'CAD', amount: 2757.25, formatted: 'C$2,757.25' }
    ],
    status: 'settlement',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-900'
  },
  {
    id: 'fees',
    label: 'Fee Collection',
    totalBalance: '$3,429.67',
    balances: [
      { currency: 'USD', amount: 2234.89, formatted: '$2,234.89' },
      { currency: 'EUR', amount: 789.34, formatted: '€789.34' },
      { currency: 'GBP', amount: 312.56, formatted: '£312.56' },
      { currency: 'CAD', amount: 92.88, formatted: 'C$92.88' }
    ],
    status: 'fees',
    bgColor: 'bg-green-50',
    textColor: 'text-green-900'
  },
  {
    id: 'maintenance',
    label: 'Maintenance Reserve',
    totalBalance: '$8,500.00',
    balances: [
      { currency: 'USD', amount: 6500.00, formatted: '$6,500.00' },
      { currency: 'EUR', amount: 1500.00, formatted: '€1,500.00' },
      { currency: 'GBP', amount: 500.00, formatted: '£500.00' }
    ],
    status: 'maintenance',
    bgColor: 'bg-muted',
    textColor: 'text-foreground'
  }
];

// Profile Data
export const TERMINAL_PROFILE_DETAILS: PaymentDetail[] = [
  { label: 'Store', value: 'Store Center 2 - 123 Vitosha Boulevard, Sofia 1000, Bulgaria' },
  { label: 'Position', value: 'Counter 1' },
  { label: 'Integration', value: 'Direct API Integration' },
  { label: 'Type', value: 'Checkout Terminal' },
  { label: 'Merchant', value: 'BRIKSTORE - MID-219389' },
  { label: 'MCC', value: '5411 - Grocery Stores, Supermarkets / Industry: Retail Food' },
  { label: 'Payment Descriptor', value: 'BRIKSTORE*SOFIA' },
  { label: 'TID', value: 'N96-VX520-2024' },
  { label: 'Model', value: 'Verifone / VX520' },
  { label: 'S/N', value: 'VX520-2024-SF-001' }
];

// Connectivity Data
export const TERMINAL_CONNECTIVITY_DETAILS: PaymentDetail[] = [
  { label: 'Primary Connection', value: 'Ethernet (RJ45)' },
  { label: 'Network IP', value: '192.168.1.145' },
  { label: 'MAC Address', value: '00:1B:44:11:3A:B7' },
  { label: 'Gateway', value: '192.168.1.1' },
  { label: 'DNS Servers', value: '8.8.8.8, 8.8.4.4' },
  { label: 'Backup Connection', value: 'Wi-Fi (802.11n)' },
  { label: 'Wi-Fi Network', value: 'StoreNet-Secure' },
  { label: 'Signal Strength', value: '-45 dBm (Excellent)' },
  { label: 'Data Encryption', value: 'TLS 1.3, AES-256' },
  { label: 'VPN Status', value: 'Connected (VPN Gateway: vpn.company.com)' },
  { label: 'Last Connection Test', value: 'January 15, 2025 14:25:33' },
  { label: 'Connection Uptime', value: '99.8% (Last 30 days)' }
];

// Hardware Data
export const TERMINAL_HARDWARE_DETAILS: PaymentDetail[] = [
  { label: 'Model', value: 'Verifone VX520' },
  { label: 'Serial Number', value: 'VX520-2024-ABC123' },
  { label: 'Manufacturer', value: 'Verifone Systems' },
  { label: 'Processor', value: 'ARM Cortex-A9 400MHz' },
  { label: 'Memory', value: '512MB RAM / 4GB Storage' },
  { label: 'Display', value: '3.5" Color LCD (320x240)' },
  { label: 'Card Reader', value: 'Magnetic Stripe, Chip (EMV), Contactless (NFC)' },
  { label: 'Keypad', value: 'Numeric with Function Keys' },
  { label: 'Power', value: 'External AC Adapter (12V 2A)' },
  { label: 'Operating System', value: 'Verifone VOS' },
  { label: 'Certification', value: 'PCI PTS 5.x, EMV Level 1 & 2' },
  { label: 'Warranty', value: 'Active until March 2026' }
];

// Events Data
export const TERMINAL_EVENTS_DATA: EventData[] = [
  {
    id: 'term_evt_1',
    eventType: 'terminal.activated',
    description: 'Terminal POS-001 successfully activated and ready for transactions',
    timestamp: '2/12/25, 4:36:18 PM',
    requestId: 'req_term_act_001',
    status: 'succeeded'
  },
  {
    id: 'term_evt_2',
    eventType: 'transaction.processed',
    description: 'Card payment of $125.50 processed successfully via contactless EMV',
    timestamp: '2/12/25, 4:35:45 PM',
    requestId: 'req_txn_proc_456',
    status: 'approved'
  },
  {
    id: 'term_evt_3',
    eventType: 'settlement.completed',
    description: 'Daily settlement batch completed - 47 transactions totaling $3,247.89',
    timestamp: '2/12/25, 4:30:00 PM',
    requestId: 'req_settle_789',
    status: 'succeeded'
  },
  {
    id: 'term_evt_4',
    eventType: 'firmware.updated',
    description: 'Terminal firmware updated to version 2.4.1 - Enhanced security features',
    timestamp: '2/12/25, 3:15:22 PM',
    requestId: 'req_fw_update_234',
    status: 'succeeded'
  },
  {
    id: 'term_evt_5',
    eventType: 'transaction.declined',
    description: 'Card transaction declined - Insufficient funds on customer card ending in 7890',
    timestamp: '2/12/25, 2:47:33 PM',
    requestId: 'req_txn_decline_567',
    status: 'failed'
  },
  {
    id: 'term_evt_6',
    eventType: 'maintenance.scheduled',
    description: 'Scheduled maintenance window initiated for terminal hardware diagnostics',
    timestamp: '2/12/25, 2:00:00 PM',
    requestId: 'req_maint_890',
    status: 'processing'
  },
  {
    id: 'term_evt_7',
    eventType: 'chargeback.received',
    description: 'Chargeback initiated for transaction $89.99 - Customer dispute received',
    timestamp: '2/12/25, 1:22:15 PM',
    requestId: 'req_cb_dispute_123',
    status: 'pending'
  },
  {
    id: 'term_evt_8',
    eventType: 'network.reconnected',
    description: 'Terminal reconnected to payment network after brief connectivity issue',
    timestamp: '2/12/25, 12:45:30 PM',
    requestId: 'req_net_recon_345',
    status: 'verified'
  },
  {
    id: 'term_evt_9',
    eventType: 'security.scan',
    description: 'PCI compliance security scan completed successfully - No vulnerabilities detected',
    timestamp: '2/12/25, 11:30:00 AM',
    requestId: 'req_sec_scan_678',
    status: 'verified'
  },
  {
    id: 'term_evt_10',
    eventType: 'terminal.deactivated',
    description: 'Terminal temporarily deactivated for maintenance and cleaning procedures',
    timestamp: '2/12/25, 10:15:45 AM',
    requestId: 'req_term_deact_901',
    status: 'processing'
  },
  {
    id: 'term_evt_11',
    eventType: 'receipt.printed',
    description: 'Customer receipt printed successfully for transaction #TXN789456123',
    timestamp: '2/12/25, 9:42:18 AM',
    requestId: 'req_receipt_234',
    status: 'succeeded'
  },
  {
    id: 'term_evt_12',
    eventType: 'error.hardware',
    description: 'Card reader hardware error detected during transaction attempt. Customer advised to use alternative payment method.',
    timestamp: '2/12/25, 9:15:22 AM',
    requestId: 'req_hw_error_567',
    status: 'failed'
  }
];

// Software Data
export const TERMINAL_SOFTWARE_DETAILS: PaymentDetail[] = [
  { label: 'Application', value: 'Verifone Payment App v3.2.1' },
  { label: 'Firmware', value: 'VOS 2.4.1.build.8834' },
  { label: 'OS Version', value: 'Verifone OS 2.0.3' },
  { label: 'Kernel', value: 'Linux 4.14.98-verifone' },
  { label: 'Security Patch', value: 'January 2025' },
  { label: 'EMV Kernel', value: 'EMV L2 Kernel 3.1.0' },
  { label: 'Contactless Kernel', value: 'cless 4.2.0 (Visa, MC, Amex, Discover)' },
  { label: 'TMS Agent', value: 'TMS Client 1.8.5' },
  { label: 'Last Update', value: 'January 14, 2025 02:05:00 AM' },
  { label: 'Update Channel', value: 'Stable' },
  { label: 'Auto-Update', value: 'Enabled (Maintenance Window: 2:00–4:00 AM)' },
  { label: 'Next Scheduled Update', value: 'February 28, 2025' }
];

// Order Data
export const TERMINAL_ORDER_DETAILS: PaymentDetail[] = [
  { label: 'Order ID', value: 'ORD-2024-TRM-00847' },
  { label: 'Order Date', value: 'December 5, 2024' },
  { label: 'Order Status', value: 'Fulfilled' },
  { label: 'Shipping Method', value: 'Express Delivery' },
  { label: 'Tracking Number', value: '1Z999AA10123456784' },
  { label: 'Delivery Date', value: 'December 10, 2024' },
  { label: 'Received By', value: 'John Smith (Store Manager)' },
  { label: 'Supplier', value: 'Verifone Distribution Center — Atlanta, GA' },
  { label: 'Invoice Number', value: 'INV-VF-2024-12847' },
  { label: 'Unit Price', value: '$349.00' },
  { label: 'Warranty Plan', value: '3-Year Extended (Active until Dec 2027)' },
  { label: 'Return Window', value: 'Closed (30-day window expired)' }
];

// Risk Data
export const TERMINAL_RISK_CHECKS: RiskCheck[] = [
  {
    status: 'success',
    title: 'Hardware Integrity',
    description: 'Terminal hardware passed all security checks. Encryption modules are functioning properly, tamper detection is active, and all security components are verified. Device firmware is up to date with latest security patches.',
    score: 0
  },
  {
    status: 'success',
    title: 'Network Connectivity',
    description: 'Network connection is stable and secure. TLS encryption is properly configured, connection latency is within acceptable limits (< 200ms), and backup connectivity options are available. No suspicious network activity detected.',
    score: 0
  },
  {
    status: 'warning',
    title: 'Security Compliance',
    description: 'PCI DSS compliance certification expires in 30 days. Terminal meets current security standards but requires recertification. Security audit scheduled for next week. All security logs are clean with no violations detected.',
    score: 25
  },
  {
    status: 'success',
    title: 'Transaction Processing',
    description: 'Transaction processing is operating normally. Payment gateway connections are stable, transaction success rate is 99.2%, and response times are optimal. No failed authentication attempts in the last 24 hours.',
    score: 0
  },
  {
    status: 'error',
    title: 'Software Version',
    description: 'Terminal software version is outdated and contains known security vulnerabilities. Critical security update available but not yet installed. This poses a potential risk to transaction security and PCI compliance.',
    score: 45
  }
];

// Timeline Data
export function generateTerminalTimelineEvents(_terminalData?: unknown): BaseTimelineEvent[] {
  return [
    {
      id: 'term_001',
      title: 'Payment Processed',
      timestamp: '1/15/25, 3:45:00 PM',
      details: [
        {
          action: 'Credit card transaction completed',
          timestamp: '1/15/25, 3:45:00 PM'
        },
        {
          action: 'Receipt printed successfully',
          timestamp: '1/15/25, 3:45:15 PM'
        }
      ]
    },
    {
      id: 'term_002',
      title: 'Connection Restored',
      timestamp: '1/15/25, 2:30:00 PM',
      details: [
        {
          action: 'Network connectivity reestablished',
          timestamp: '1/15/25, 2:30:00 PM'
        },
        {
          action: 'Pending transactions synced',
          timestamp: '1/15/25, 2:30:30 PM'
        }
      ]
    },
    {
      id: 'term_003',
      title: 'Offline Mode Activated',
      timestamp: '1/15/25, 1:15:00 PM',
      details: [
        {
          action: 'Network connection lost',
          timestamp: '1/15/25, 1:15:00 PM'
        },
        {
          action: 'Terminal switched to offline mode',
          timestamp: '1/15/25, 1:15:05 PM'
        }
      ]
    },
    {
      id: 'term_004',
      title: 'Hardware Diagnostic Passed',
      timestamp: '1/15/25, 9:00:00 AM',
      details: [
        {
          action: 'Card reader test completed',
          timestamp: '1/15/25, 9:00:00 AM'
        },
        {
          action: 'Printer functionality verified',
          timestamp: '1/15/25, 9:00:30 AM'
        },
        {
          action: 'All hardware components operational',
          timestamp: '1/15/25, 9:01:00 AM'
        }
      ]
    },
    {
      id: 'term_005',
      title: 'Security Key Rotated',
      timestamp: '1/14/25, 11:30:00 PM',
      details: [
        {
          action: 'Encryption keys updated',
          timestamp: '1/14/25, 11:30:00 PM'
        },
        {
          action: 'Terminal authentication refreshed',
          timestamp: '1/14/25, 11:30:15 PM'
        }
      ]
    },
    {
      id: 'term_006',
      title: 'Firmware Update Installed',
      timestamp: '1/14/25, 2:00:00 AM',
      details: [
        {
          action: 'Firmware v2.4.1 downloaded',
          timestamp: '1/14/25, 2:00:00 AM'
        },
        {
          action: 'Update installation completed',
          timestamp: '1/14/25, 2:05:00 AM'
        },
        {
          action: 'Terminal reboot successful',
          timestamp: '1/14/25, 2:07:00 AM'
        }
      ]
    },
    {
      id: 'term_007',
      title: 'Daily Settlement Processed',
      timestamp: '1/13/25, 11:59:00 PM',
      details: [
        {
          action: 'Batch closed with 47 transactions',
          timestamp: '1/13/25, 11:59:00 PM'
        },
        {
          action: 'Settlement report generated',
          timestamp: '1/13/25, 11:59:30 PM'
        }
      ]
    },
    {
      id: 'term_008',
      title: 'Terminal Activated',
      timestamp: '1/10/25, 10:00:00 AM',
      details: [
        {
          action: 'Terminal registration completed',
          timestamp: '1/10/25, 10:00:00 AM'
        },
        {
          action: 'Initial configuration applied',
          timestamp: '1/10/25, 10:02:00 AM'
        },
        {
          action: 'First test transaction successful',
          timestamp: '1/10/25, 10:05:00 AM'
        }
      ]
    }
  ];
}

// Transaction Data
export const TERMINAL_TRANSACTIONS_DATA: TransactionData[] = [
  {
    id: 'term-txn-001',
    risk: 5,
    transactionId: 'POS-2024-TRM-001',
    type: 'Payment',
    amount: '$45.99',
    paymentMethod: 'Credit Card •••• 1234',
    customer: 'Walk-in Customer',
    date: 'Dec 15'
  },
  {
    id: 'term-txn-002',
    risk: 10,
    transactionId: 'POS-2024-TRM-002',
    type: 'Payment',
    amount: '$127.50',
    paymentMethod: 'Debit Card •••• 5678',
    customer: 'Sarah Johnson',
    date: 'Dec 15'
  },
  {
    id: 'term-txn-003',
    risk: 20,
    transactionId: 'REF-2024-TRM-001',
    type: 'Refund',
    amount: '-$25.00',
    paymentMethod: 'Credit Card •••• 9012',
    customer: 'Mike Chen',
    date: 'Dec 14'
  },
  {
    id: 'term-txn-004',
    risk: 8,
    transactionId: 'POS-2024-TRM-003',
    type: 'Payment',
    amount: '$89.99',
    paymentMethod: 'Contactless Payment',
    customer: 'Emma Davis',
    date: 'Dec 14'
  },
  {
    id: 'term-txn-005',
    risk: 15,
    transactionId: 'POS-2024-TRM-004',
    type: 'Payment',
    amount: '$312.75',
    paymentMethod: 'Chip & PIN',
    customer: 'Robert Wilson',
    date: 'Dec 13'
  },
  {
    id: 'term-txn-006',
    risk: 60,
    transactionId: 'DIS-2024-TRM-001',
    type: 'Chargeback',
    amount: '-$78.50',
    paymentMethod: 'Credit Card •••• 3456',
    customer: 'Lisa Brown',
    date: 'Dec 13'
  },
  {
    id: 'term-txn-007',
    risk: 12,
    transactionId: 'POS-2024-TRM-005',
    type: 'Payment',
    amount: '$156.25',
    paymentMethod: 'Mobile Wallet',
    customer: 'Alex Thompson',
    date: 'Dec 12'
  },
  {
    id: 'term-txn-008',
    risk: 7,
    transactionId: 'POS-2024-TRM-006',
    type: 'Payment',
    amount: '$67.80',
    paymentMethod: 'Tap to Pay',
    customer: 'Jennifer Lee',
    date: 'Dec 12'
  },
  {
    id: 'term-txn-009',
    risk: 25,
    transactionId: 'REF-2024-TRM-002',
    type: 'Refund',
    amount: '-$42.00',
    paymentMethod: 'Debit Card •••• 7890',
    customer: 'David Garcia',
    date: 'Dec 11'
  },
  {
    id: 'term-txn-010',
    risk: 3,
    transactionId: 'POS-2024-TRM-007',
    type: 'Payment',
    amount: '$234.50',
    paymentMethod: 'EMV Chip Card',
    customer: 'Maria Rodriguez',
    date: 'Dec 11'
  }
];